<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { useChatStore } from '@/stores/chat'
import { Send, X, User, Headphones } from 'lucide-vue-next'

const chatStore = useChatStore()
const messageInput = ref('')
const messagesContainer = ref<HTMLElement | null>(null)

const session = computed(() => chatStore.selectedSession)

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

watch(() => chatStore.messages.length, scrollToBottom)

async function handleSend() {
  const text = messageInput.value.trim()
  if (!text || !chatStore.selectedSessionId) return
  messageInput.value = ''
  await chatStore.sendMessage(chatStore.selectedSessionId, text)
  scrollToBottom()
}

function handleEnd() {
  if (chatStore.selectedSessionId) {
    chatStore.endSession(chatStore.selectedSessionId)
  }
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
</script>

<template>
  <div v-if="!session" class="flex-1 flex items-center justify-center text-surface-500">
    <p class="text-sm">Select a chat to view</p>
  </div>

  <div v-else class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-2.5 border-b border-surface-700 bg-surface-800/50">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <User class="w-4 h-4 text-surface-400 flex-shrink-0" />
          <span class="text-sm font-medium text-surface-200 truncate">{{ session.visitor_name || 'Visitor' }}</span>
        </div>
        <div class="text-xs text-surface-500 mt-0.5">
          {{ session.visitor_email || '' }}
          <span v-if="session.client_name"> &middot; {{ session.client_name }}</span>
        </div>
      </div>
      <button
        @click="handleEnd"
        class="flex items-center gap-1 px-2 py-1 text-xs text-danger-400 hover:text-danger-300 hover:bg-danger-500/10 rounded transition-colors"
        title="End chat"
      >
        <X class="w-3.5 h-3.5" />
        End
      </button>
    </div>

    <!-- Messages -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto px-4 py-3 space-y-3">
      <div v-if="chatStore.messagesLoading" class="text-center text-sm text-surface-500 py-4">Loading messages...</div>

      <div
        v-for="msg in chatStore.messages"
        :key="msg.id"
        class="flex"
        :class="msg.sender_type === 'agent' ? 'justify-end' : msg.sender_type === 'system' ? 'justify-center' : 'justify-start'"
      >
        <!-- System message -->
        <div v-if="msg.sender_type === 'system'" class="text-xs text-surface-500 italic py-1">
          {{ msg.message }}
        </div>

        <!-- Visitor message -->
        <div v-else-if="msg.sender_type === 'visitor'" class="max-w-[80%]">
          <div class="bg-surface-700 rounded-lg rounded-bl-sm px-3 py-2">
            <p class="text-sm text-surface-200 whitespace-pre-wrap break-words">{{ msg.message }}</p>
          </div>
          <span class="text-[10px] text-surface-600 mt-0.5 block">{{ formatTime(msg.created_at) }}</span>
        </div>

        <!-- Agent message -->
        <div v-else class="max-w-[80%]">
          <div class="bg-primary-600/20 border border-primary-500/20 rounded-lg rounded-br-sm px-3 py-2">
            <p class="text-sm text-surface-200 whitespace-pre-wrap break-words">{{ msg.message }}</p>
          </div>
          <span class="text-[10px] text-surface-600 mt-0.5 block text-right">{{ formatTime(msg.created_at) }}</span>
        </div>
      </div>
    </div>

    <!-- Input -->
    <div class="px-4 py-3 border-t border-surface-700">
      <form @submit.prevent="handleSend" class="flex items-center gap-2">
        <input
          v-model="messageInput"
          type="text"
          placeholder="Type a message..."
          class="flex-1 bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 placeholder-surface-500 focus:outline-none focus:border-primary-500 transition-colors"
          :disabled="session.status !== 'active'"
        />
        <button
          type="submit"
          :disabled="!messageInput.trim() || session.status !== 'active'"
          class="p-2 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          <Send class="w-4 h-4" />
        </button>
      </form>
    </div>
  </div>
</template>
